import BaseUIHandler from "../BaseUIHandler";
import Game from "Game";
import { PlayerActions } from "State/Player/PlayerActions";
import BaseUISystem, { UIEvent } from "../BaseUISystem";
import { BoosterType } from "State/Card/BoosterType";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Container from "Prototype/Container";
import Point from "Utility/Point";
import Renderable from "Component/Renderable";
import Sprite from "Prototype/Sprite";
import RelativeButton, { IRelativeButtonData } from "Prototype/UI/BaseElements/RelativeButton";
import ButtonComponent from "Component/UI/ButtonComponent";
import { StartAnimation, StartAnimationClip } from "../../Animation/AnimationHelper";
import Text from "Prototype/Text";
import TransformComponent from "Component/Transform";
import RenderSystem, { RenderSystemEvent } from "../../Render/RenderSystem";
import ToolboxItem from "Prototype/UI/Level/ToolboxItem";
import Interactable from "Component/Interactable";
import { PlaySound } from "../../Audio/AudioHelper";
import SpeechBubble from "Prototype/World/SpeechBubble";
import { CardActions } from "State/Card/CardActions";
import { UIActions } from "State/UI/UIActions";
import { Easing } from "@tweenjs/tween.js";
import { AnimationClipEvent } from "../../Animation/AnimationClip";
import { FTUXType } from "Player/PlayerProperty/Progress/FTUXType";
import ColorFilter from "Component/ColorFilter";
import SpriteComponent from "Component/Render/Sprite";
import { LEVEL_START } from "../../Card/Task/LevelIntroTask";
import ElementComponent from "Component/UI/Element";
import { trackResource } from "Utility/Tracking/TrackingHelpers/Transaction";
import { isShopSupported } from "Utility/Platform";
import { ILevelStateTaskData } from "State/Level/ILevelState";
import { IGameState } from "State/Game/IGameState";
import { Deferred } from "Utility/Deferred";
import { FTUXStateHelper } from "State/Game/FTUX/FTUXStateHelper";
import { PopupID } from "State/UI/PopupID";
import PIXIRenderer from "System/Render/PIXIRenderer";
import { Alpha } from "System/Animation/Clip";
import { LevelHud } from "System/UI/HUD/LevelHud";

export default class BoosterHUDHandler extends BaseUIHandler {
    protected boosterStartingPosition: Point = new Point(0, -180);
    protected boosterItems: ToolboxItem[];
    protected itemSpacing: number = 200;
    protected _open: boolean = false;
    protected opening: boolean = false;
    protected ftux: FTUXType = null;
    protected closing: boolean = false;
    public static tutorialPlayingDeferred: Deferred | null = null;
    private levelHudLayout: any;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.BOOSTER_HUD, uiSystem, false);

        this.alwaysVisible = true;
        this.system.scene.on('ToolboxFTUX', this.showFTUX.bind(this));

        this.system.scene.on(
            UIEvent.RESTORE_INTERACTIVITY,
            this.restoreInteractivity.bind(this)
        );
        this.system.scene.on(
            UIEvent.DISABLE_INTERACTIVITY,
            this.disableInteractivity.bind(this)
        );
        this.system.scene.on(LEVEL_START, () => {
            this.levelHudLayout.boostersButton.getComponent(Interactable).enabled = true;
        });

        BoosterHUDHandler.tutorialPlayingDeferred = null;
    }

    protected onCreate() {
        this.levelHudLayout = LevelHud.instance.getLayout();
        this.element.addChild(this.levelHudLayout.boostersContainer);
        this.element.getComponent(ElementComponent).pivot.copy(Point.Zero);
        super.onCreate();

        this.createElements();

        if (Game.store.getState().levelState.levelNumber < 8)
            this.element.getComponent<Renderable>(Renderable).visible = false;

        RenderSystem.on(RenderSystemEvent.RESIZE, this.onResize);
        this.onResize();
    }

    protected createElements(): void {
        const state: IGameState = Game.store.getState();
        const alreadyUnlocked: boolean = FTUXStateHelper.isFtuxCompleted(
            state.playerState,
            FTUXType.TOOLBOX_UNLOCK
        );

        const gasCan: ToolboxItem = GameObject.Instantiate(ToolboxItem, {
            type: BoosterType.TNT,
            FTUX: FTUXType.TOOLBOX_UNLOCK,
            ...this.boosterStartingPosition,
            onClick: this.buyBooster.bind(this, BoosterType.TNT),
            alpha: 0,
            visible: false,
        });

        const slingShot: ToolboxItem = GameObject.Instantiate(ToolboxItem, {
            type: BoosterType.BULLS_EYE,
            FTUX: FTUXType.BULLSEYE_BOOSTER,
            ...this.boosterStartingPosition,
            onClick: this.buyBooster.bind(this, BoosterType.BULLS_EYE),
            alpha: 0,
            visible: false,
        });

        const fan: ToolboxItem = GameObject.Instantiate(ToolboxItem, {
            type: BoosterType.SHUFFLE,
            FTUX: FTUXType.SHUFFLE_BOOSTER,
            ...this.boosterStartingPosition,
            onClick: this.buyBooster.bind(this, BoosterType.SHUFFLE),
            alpha: 0,
            visible: false,
        });

        this.setupToolboxButton(alreadyUnlocked);

        this.boosterItems = [gasCan, slingShot, fan];
        this.boosterItems.forEach((item: ToolboxItem) =>
            this.levelHudLayout.boostersContainer.addChild(item)
        );

        if (!alreadyUnlocked) {
            this.createFTUXElements();
        }
    }

    protected setupToolboxButton(unlocked: boolean): void {
        this.levelHudLayout.boostersContainer.getComponent(Renderable).visible = unlocked;
        this.levelHudLayout.boostersButton.getComponent(Renderable).visible = unlocked;
        this.levelHudLayout.boostersButton.on(GameObjectEvent.SELECTED, this.openToolbox.bind(this));
    }

    protected createFTUXElements(): void {
        const container: GameObject = GameObject.Instantiate(
            Container,
            this.toolboxContainerParams
        );

        const text: GameObject = GameObject.Instantiate(Text, {
            name: 'unlocked_text',
            text: Game.locale.getText('SuitcaseTutorial'),
            style: Game.style.getStyle('ftux.text'),
            bounds: new Point(1200, 880),
            scale: Point.One,
            relativePosition: new Point(0.5, 0.65),
            anchor: Point.Center,
        });
        text.addComponent<Renderable>(Renderable, {
            alpha: 0,
        });

        const ftuxIcon: GameObject = GameObject.Instantiate(Sprite, {
            name: 'unlocked_item',
            texture: 'toolbox_ftux',
            relativePosition: new Point(0.5, 0.5),
            anchor: new Point(0.45, 1.1),
        });
        const ftuxBtn: GameObject = GameObject.Instantiate(
            RelativeButton,
            this.toolboxBtnParams
        );
        ftuxBtn.getComponentsInChildren(
            ButtonComponent
        )[0].onClick = this.unlockToolbox.bind(this);
        ftuxBtn.getComponent(Renderable).alpha = 0;

        const ftuxShine: GameObject = GameObject.Instantiate(Sprite, {
            name: 'shine',
            texture: 'shine_2',
            relativePosition: new Point(0.5, 0.37),
            anchor: new Point(0.5, 0.5),
        });
        ftuxShine.getComponent(SpriteComponent).alpha = 0;

        container.addChild(ftuxShine);
        container.addChild(text);
        container.addChild(ftuxBtn);
        container.addChild(ftuxIcon);

        this.element.addChild(container);
    }

    protected showFTUX(params: {
        type: FTUXType;
        taskData: ILevelStateTaskData;
    }): void {
        if (this.ftux) return;
        this.ftux = params.type;

        // if we have an FTUX then we should probably show the button
        if(this.ftux !== FTUXType.TOOLBOX_UNLOCK) {
            this.setupToolboxButton(true);
        }

        this.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY);
        BoosterHUDHandler.tutorialPlayingDeferred = new Deferred();

        // Create the speechbubble
        const speechBubble = GameObject.Instantiate(
            SpeechBubble,
            this.speechBubbleParams
        );
        const boosterIcon: GameObject = this.levelHudLayout.boostersButton;
        boosterIcon.addChild(speechBubble);

        this.showOverlay();

        this.levelHudLayout.boostersContainer.getComponent(Renderable).visible = true;

        if (FTUXType.TOOLBOX_UNLOCK === params.type) {
            this.showUnlockToolbox();
        } else {
            this.unlockBooster(params.type);
        }
    }
    private showUnlockToolbox(): void {
        this.system.scene.off('ToolboxFTUX');
        const ftux: GameObject = this.element.find(
            this.toolboxContainerParams.name
        );
        ftux.getComponent(Renderable).visible = true;

        StartAnimation(ftux, 'ToolboxTutorialOpen');
    }
    private unlockToolbox(): void {
        const ftux: GameObject = this.element.find(
            this.toolboxContainerParams.name
        );
        const destinationObject: GameObject = this.levelHudLayout.boostersButton;
        const speechBubble: GameObject = destinationObject.find(
            this.speechBubbleParams.name
        );

        StartAnimation(ftux, 'ToolboxTutorialClose', {
            destinationObject,
            speechBubble,
        });

        PlayerActions.setFTUXUnlocks([FTUXType.TOOLBOX_UNLOCK]);
    }
    private unlockBooster(unlockType: FTUXType): void {
        const speechBubble: GameObject = GameObject.findByName(
            this.speechBubbleParams.name
        )[0];
        const speechBubbleRenderable: Renderable = speechBubble.getComponent<
            Renderable
        >(Renderable);
        speechBubbleRenderable.visible = true;

        StartAnimation(speechBubble, 'Alpha', {
            startAlpha: 0,
            endAlpha: 1,
            duration: 350,
        });
    }
    private openToolbox(): void {
        if (this.opening) return;

        if (this.open || this.closing) {
            this.closeMenu(undefined);
            return;
        }

        this.open = true;
        this.opening = true;

        // Switch between which toolbox is shown
        const openToolbox: GameObject = this.levelHudLayout.boostersOpened;
        const closedToolbox: GameObject = this.levelHudLayout.boostersButton;

        StartAnimation(openToolbox, 'Alpha', {
            startAlpha: 0,
            endAlpha: 1,
            duration: 350,
        });

        StartAnimation(closedToolbox, 'Alpha', {
            startAlpha: 1,
            endAlpha: 0,
            duration: 350,
        }).on(AnimationClipEvent.COMPLETE, () => {
            closedToolbox.getComponent(Interactable).toggle(!this.ftux);
        });

        // If there isn't a ftux event then open the overlay
        if (!this.ftux) {
            this.showOverlay();
        } else {
            // If there is then unlock the ftux
            const speechBubbles: GameObject[] = GameObject.findByName(
                this.speechBubbleParams.name
            );
            if (speechBubbles.length > 0) {
                StartAnimationClip(
                    new Alpha(speechBubbles[0], {
                        startAlpha: 1,
                        endAlpha: 0,
                        duration: 350,
                    }).on(AnimationClipEvent.DESTROY, () => {
                        speechBubbles[0].destroy();
                    })
                );
            }

            PlayerActions.setFTUXUnlocks([this.ftux]);
        }

        const state: IGameState = Game.store.getState();

        this.boosterItems.forEach((item, index) => {
            item.hideDescription();

            this.setBoosterVisibility(item);
            this.setBoosterPrice(item, state);
            this.resetBoosterPosition(item);

            StartAnimation(item, 'BoosterItemReveal', {
                index,
                spacing: this.itemSpacing,
                offset: new Point(0, -80),
                visibility: this.setBoosterVisibility(item),
                onComplete: () => {
                    this.opening = false;
                },
            });
        });

        PlaySound('tool-box-open');
    }
    private buyBooster(type: BoosterType): void {
        const state = this.system.store.getState();

        this.closeMenu();

        if (
            state.playerState.inventory[type] > 0 ||
            state.playerState.inventory.coins >=
                state.levelState.boosterPrices[type]
        ) {
            CardActions.playBooster(type);
            PlaySound(type);

            switch (type) {
                case BoosterType.SHUFFLE: {
                    PlaySound('shuffle');
                    break;
                }

                case BoosterType.TNT: {
                    PlaySound('canister');
                    break;
                }

                case BoosterType.BULLS_EYE: {
                    PlaySound('slingshot');
                    break;
                }

                default: {
                    console.warn(`sound for ${type} has not been setup`);
                }
            }

            trackResource(Game.store.getState());
        } else {
            !isShopSupported()
                ? UIActions.showPopup(PopupID.NO_COINS_MENU, {})
                : UIActions.showPopup(PopupID.SHOP_MENU, {});
        }

        this.open = false;
        this.opening = false;
    }

    private setBoosterVisibility(
        boosterItem: ToolboxItem
    ): {
        saturation: number;
        enabled: boolean;
        visible: boolean;
    } {
        const enabled: boolean =
            boosterItem.isUnlocked ||
            (this.ftux && boosterItem.FTUX_Type === this.ftux);
        const colorFilter: ColorFilter = boosterItem.getComponent(ColorFilter);
        const interactable: Interactable = boosterItem.getComponent(
            Interactable
        );
        const renderable: Renderable = boosterItem.getComponent<Renderable>(
            Renderable
        );

        renderable.alpha = Number(enabled);

        if (this.ftux && boosterItem.FTUX_Type !== this.ftux) {
            colorFilter.saturation = 0;
            interactable.enabled = false;
            renderable.visible = boosterItem.isUnlocked;

            return {
                saturation: 0,
                enabled: false,
                visible: boosterItem.isUnlocked,
            };
        } else {
            colorFilter.saturation = 0;
            interactable.enabled = false;
            renderable.visible = enabled;

            return {
                saturation: 1,
                enabled: enabled,
                visible: enabled,
            };
        }
    }
    private setBoosterPrice(boosterItem: ToolboxItem, state: IGameState): void {
        const count: number = state.playerState.inventory[
            boosterItem.boosterType
        ] as number;
        if (count > 0) {
            boosterItem.cost = `${Game.locale.getText('Free')} x${count}`;
        } else {
            boosterItem.cost = state.levelState.boosterPrices[
                boosterItem.boosterType
            ].toString();
        }
    }
    private resetBoosterPosition(boosterItem: ToolboxItem): void {
        const transform: TransformComponent = boosterItem.getComponent(
            TransformComponent
        );
        transform.position.copy(this.boosterStartingPosition);
        transform.scale.copy(Point.Zero);
    }
    private showOverlay(): void {
        this.system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            this.system.overlayPopup
        );
        this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.element);
        this.system.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            this.system.UIOverlay
        );
        if (!this.ftux) {
            this.system.overlayPopup.on(
                GameObjectEvent.POINTER_DOWN,
                this.closeMenu.bind(this)
            );
        }

        StartAnimation(this.system.overlayPopup, 'OverlayFadeIn');
    }
    private closeMenu(allowReopen?: boolean): void {
        if (this.closing || this.opening) return;
        this.system.overlayPopup.off(GameObjectEvent.POINTER_DOWN);
        this.closing = true;
        this.open = false;
        this.opening = false;
        this.ftux = null;

        BoosterHUDHandler.tutorialPlayingDeferred?.control.resolve();

        // Hide overlay
        //this.system.overlayPopup.off(GameObjectEvent.POINTER_DOWN, this.closeMenu.bind(this));
        StartAnimation(this.system.overlayPopup, 'OverlayFadeOut');

        // Switch between which toolbox is shown
        const openToolbox: GameObject = this.levelHudLayout.boostersOpened;
        const closedToolbox: GameObject = this.levelHudLayout.boostersButton;

        StartAnimation(openToolbox, 'Alpha', {
            startAlpha: 1,
            endAlpha: 0,
            duration: 350,
        });
        StartAnimation(closedToolbox, 'Alpha', {
            startAlpha: 0,
            endAlpha: 1,
            duration: 350,
        }).on(AnimationClipEvent.COMPLETE, () => {
            // Re-enable in case it was disabled for the ftux
            if (allowReopen) {
                closedToolbox.getComponent(Interactable).enabled = true;
            }
        });

        // Hide booster choices
        this.boosterItems.forEach((item: ToolboxItem, index: number) => {
            StartAnimation(item, 'Scale', {
                startScale: Point.One,
                endScale: Point.Zero,
                easing: Easing.Quadratic.InOut,
                duration: 350,
                delay: 50 * index,
            }).on(AnimationClipEvent.DESTROY, () => {
                this.closing = false;
                item.enabled = false;
            });
        });
        PlaySound('tool-box-close');
    }

    private set open(value: boolean) {
        this._open = value;
    }
    private get open(): boolean {
        return this._open;
    }

    public onResize = (): void => {
        if (this.element.destroyed) return;

        this.levelHudLayout.boostersContainer
            .getComponent(TransformComponent).position
            .set(PIXIRenderer.renderer.width - 100, PIXIRenderer.renderer.height);

        this.element.container.resize(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
    };

    protected toolboxContainerParams: any = {
        name: 'toolbox_ftux',
        //relativePosition: new Point(0.5, 0.5),
        anchors: [0, 0, 0, 0],
        visible: false,
    };

    protected toolboxBtnParams: IRelativeButtonData = {
        name: 'continue_btn_container',
        background: {
            name: 'continue_btn_bg',
            cacheKey: 'blank',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
            },
        },
        button: {
            name: 'continue_btn',
            cacheKey: 'button_medium_green',
            layout: {
                anchors: [0.5, 0.52, 0.5, 0.52],
                pivot: [0.5, 0.5],
            },
        },
        text: {
            name: 'continue_btn_txt',
            textLocalizationKey: 'Continue',
            style: Game.style.getStyle('popup.green_btn'),
            layout: {
               offsets: [0,6,0,6],
            },
        },
        relativePosition: new Point(0.5, 0.85),
        onClick: this.unlockToolbox.bind(this),
    };
    protected speechBubbleParams: any = {
        name: 'booster_speech_bubble',
        x: -30,
        y: -20,
        text: Game.locale.getText('TryMe'),
        textStyle: Game.style.getStyle('ftux.speech_bubble'),
        visible: true,
        alpha: 0,
    };
}
